import React, { useEffect, useMemo, useState } from "react";
import {Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

export default function AllMaintenance() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  // useEffect(() => {
  //   const roles = JSON.parse(localStorage.getItem("roles") || "[]");
  //   if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
  //   if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  // }, [navigate, token]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-maintenance?page=0&size=200`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRecords(Array.isArray(data) ? data : data.content || []);
    } catch { setRecords([]); toast.error("Failed to load maintenance records."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchRecords(); }, [token]);

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      if (typeFilter) params.set("type", typeFilter);
      const res = await fetch(`${API_BASE_URL}/api/asset-maintenance/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download report");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      const hasFilters = search || statusFilter !== "ALL" || typeFilter;
      a.href = url;
      a.download = hasFilters ? `Maintenance_Filtered_${date}.xlsx` : `Maintenance_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-maintenance/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(status === "CLOSED" ? "Maintenance closed. Asset restored to Available." : "Marked as In Progress.");
      fetchRecords();
    } catch { toast.error("Failed to update status."); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter(r => {
      const matchStatus = statusFilter === "ALL" || (r.status || "OPEN") === statusFilter;
      const matchType = !typeFilter || (r.maintenanceType || "") === typeFilter;
      if (!q) return matchStatus && matchType;
      return matchStatus && matchType && (
        (r.assetCode || "").toLowerCase().includes(q) ||
        (r.assetName || "").toLowerCase().includes(q) ||
        (r.vendorName || "").toLowerCase().includes(q) ||
        (r.maintenanceType || "").toLowerCase().includes(q)
      );
    });
  }, [records, search, statusFilter, typeFilter]);

  const open = records.filter(r => !r.status || r.status === "OPEN").length;
  const inProgress = records.filter(r => r.status === "IN_PROGRESS").length;
  const closed = records.filter(r => r.status === "CLOSED").length;

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-tools me-2"></i> All Maintenance</h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>View all service requests, track repair status, and manage vendor repair history.</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={() => navigate("/newmaintenance")}>
              <i className="bi bi-plus-circle me-1"></i> Raise Request
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allassets")}>
              <i className="bi bi-box-seam me-1"></i> Asset List
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={downloadReport} disabled={downloading}>
              <i className="bi bi-download me-1"></i>{downloading ? "Downloading..." : "Export Excel"}
            </button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Total Records", value: records.length, icon: "bi-tools", color: "#1e3a5f" },
            { label: "Open", value: open, icon: "bi-exclamation-circle", color: "#dc3545" },
            { label: "In Progress", value: inProgress, icon: "bi-gear", color: "#fd7e14" },
            { label: "Closed", value: closed, icon: "bi-check-circle", color: "#198754" },
          ].map(item => (
            <div className="col-md-3 col-6" key={item.label}>
              <div className="bg-white border rounded p-2 d-flex align-items-center gap-2">
                <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: 20 }}></i>
                <div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{item.label}</div>
                  <div className="fw-bold" style={{ fontSize: 18, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters(p => !p)}>
          <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
        </div>
        {showFilters && (
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input type="text" className="form-control form-control-sm"
                placeholder="Search by asset, vendor, type..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="ALL">All Statuses</option>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="BREAKDOWN">Breakdown</option>
                <option value="PREVENTIVE">Preventive</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(""); setStatusFilter("ALL"); setTypeFilter(""); }}>Clear</button>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
          <span className="text-muted" style={{ fontSize: 13 }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading maintenance records...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-tools" style={{ fontSize: 32, opacity: 0.3 }}></i>
            <div className="mt-2">No maintenance records found.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm align-middle text-start">
              <thead className="table-light">
                <tr>
                  <th>View | Edit</th>
                  <th>Asset</th><th>Type</th><th>Service Date</th><th>Vendor</th>
                  <th>Cost</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id}>
                    <td>
                          <Link to={`/viewmaintenance/${item.id}`} className="me-1 text-primary" style={{ fontSize: 16 }}>View</Link>|
                          <Link to={`/editmaintenance/${item.id}`} className="ms-1 text-primary" style={{ fontSize: 16 }}>Edit</Link>
                        </td>
                    <td>
                      <span className="fw-semibold" style={{ fontFamily: "monospace", fontSize: 13 }}>{item.assetCode}</span>
                      {item.assetName && <span className="text-muted ms-2" style={{ fontSize: 12 }}>{item.assetName}</span>}
                    </td>
                    <td><span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>{item.maintenanceType || "—"}</span></td>
                    <td>{item.serviceDate || "—"}</td>
                    <td>{item.vendorName || "—"}</td>
                    <td>{item.repairCost ? `₹${Number(item.repairCost).toLocaleString("en-IN")}` : "—"}</td>
                    <td>
                      <span className={`badge ${item.status === "CLOSED" ? "bg-success" : item.status === "IN_PROGRESS" ? "bg-warning text-dark" : "bg-secondary"}`}>
                        {item.status || "OPEN"}
                      </span>
                    </td>
                    <td>
                      {item.status !== "CLOSED" && (
                        <div className="d-flex gap-1">
                          {(!item.status || item.status === "OPEN") && (
                            <button className="btn btn-sm btn-outline-warning" onClick={() => handleStatusUpdate(item.id, "IN_PROGRESS")}>In Progress</button>
                          )}
                          <button className="btn btn-sm btn-outline-success" onClick={() => handleStatusUpdate(item.id, "CLOSED")}>Close</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
